import { useState } from 'react';
import { Check, Settings2, X } from 'lucide-react';
import type { MetricKey } from '@contracts';
import { METRIC_CATALOG } from '@contracts';
import { getCompanies } from '@/services/authService';
import { ALL_COMPANIES, companiesForMetric } from '@/lib/companyVisibility';
import { saveSourceConfig, type SourceConfig } from '@/services/sourceConfigService';

function CheckBox({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={on}
      className={`inline-flex h-6 w-6 items-center justify-center rounded-md border-2 transition ${
        on ? 'border-red-600 bg-red-600 text-white' : 'border-slate-200 text-transparent hover:border-slate-300'
      }`}
    >
      <Check className="h-3.5 w-3.5" />
    </button>
  );
}

/**
 * ⚙ Panel ADMIN: edita a qué compañías pertenece cada fuente del catálogo. Una fuente solo
 * aparece cuando alguna de sus compañías está activa en el filtro. Desmarcar TODAS sus casillas
 * la oculta en todos lados. Arranca de los overrides; lo no tocado, de la membresía derivada.
 * El BFF re-valida rol.
 */
export function SourceConfigPanel({
  overrides,
  onSaved,
  onClose,
}: {
  overrides: SourceConfig;
  onSaved: (cfg: SourceConfig) => void;
  onClose: () => void;
}) {
  // Siempre las 3 empresas (incluye SmartBamboo aunque no esté en el JWT del admin); el
  // nombre real lo toma del JWT si el usuario la tiene, si no, el canónico.
  const known = getCompanies();
  const companies = ALL_COMPANIES.map((c) => ({
    slug: c.slug,
    name: known.find((k) => k.slug === c.slug)?.name ?? c.name,
  }));
  const [cfg, setCfg] = useState<SourceConfig>(() => {
    const out: SourceConfig = {};
    for (const m of METRIC_CATALOG) out[m.key] = overrides[m.key] ?? companiesForMetric(m);
    return out;
  });
  const [saving, setSaving] = useState(false);

  const toggle = (key: MetricKey, slug: string) =>
    setCfg((c) => {
      const cur = c[key] ?? [];
      return { ...c, [key]: cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug] };
    });

  const allOn = (slug: string) => METRIC_CATALOG.every((m) => (cfg[m.key] ?? []).includes(slug));

  // Columna entera: si TODAS las fuentes la tienen → desmarca todas; si no → marca todas.
  const toggleColumn = (slug: string) =>
    setCfg((c) => {
      const turnOff = METRIC_CATALOG.every((m) => (c[m.key] ?? []).includes(slug));
      const next: SourceConfig = { ...c };
      for (const m of METRIC_CATALOG) {
        const cur = next[m.key] ?? [];
        next[m.key] = turnOff ? cur.filter((s) => s !== slug) : cur.includes(slug) ? cur : [...cur, slug];
      }
      return next;
    });

  const save = async () => {
    setSaving(true);
    const ok = await saveSourceConfig(cfg);
    setSaving(false);
    if (ok) onSaved(cfg);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85dvh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-5">
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-bold text-slate-800">Compañías por fuente</h3>
          </div>
          <button onClick={onClose} aria-label="Cerrar">
            <X className="h-5 w-5 text-slate-400 hover:text-slate-700" />
          </button>
        </div>
        <p className="px-5 pt-3 text-sm text-slate-500">
          Define a qué compañías pertenece cada fuente. Una fuente solo aparece en el catálogo
          cuando alguna de sus compañías está activa. La casilla del <b>encabezado</b> marca o
          desmarca toda la columna.
        </p>

        <div className="flex-1 overflow-auto p-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-slate-400">
                <th className="pb-2 text-left font-medium">Fuente</th>
                {companies.map((c) => (
                  <th key={c.slug} className="px-2 pb-2 text-center font-medium">
                    <div className="flex flex-col items-center gap-1.5">
                      <span>{c.name}</span>
                      <CheckBox on={allOn(c.slug)} onClick={() => toggleColumn(c.slug)} label={`Todas en ${c.name}`} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRIC_CATALOG.map((m) => (
                <tr key={m.key} className="border-t border-slate-50">
                  <td className="py-2 pr-2 text-slate-700">{m.label}</td>
                  {companies.map((c) => (
                    <td key={c.slug} className="px-2 text-center">
                      <CheckBox
                        on={(cfg[m.key] ?? []).includes(c.slug)}
                        onClick={() => toggle(m.key, c.slug)}
                        label={`${m.label} en ${c.name}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 p-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
